import React, { useEffect, useRef } from 'react';
import { useTableSync } from '../context/TableSyncContext';

export const useCustomScroll = (tableId?: string, syncWithTableIds?: string[]) => {
    const tableRef = useRef<HTMLDivElement>(null);
    const scrollbarRef = useRef<HTMLDivElement>(null);
    const scrollbarContentRef = useRef<HTMLDivElement>(null);
    const isProgrammaticScrollRef = useRef(false);
    const { registerTable, unregisterTable, getTableRef } = useTableSync();
    const lastScrollLeftRef = useRef<number>(0);

    const handleTableScroll = (source: 'table' | 'scrollbar') => {
        if (!tableRef.current || !scrollbarRef.current || isProgrammaticScrollRef.current) return;
        if (source === 'table') {
            scrollbarRef.current.scrollLeft = tableRef.current.scrollLeft;
        } else {
            tableRef.current.scrollLeft = scrollbarRef.current.scrollLeft;
        }
    };

    const createRefHandler = (ref: any) => (el: HTMLDivElement | null) => {
        if (el && ref) {
        // eslint-disable-next-line no-param-reassign
            ref.current = el;
        }
    };

    // Register/unregister table for syncing
    useEffect(() => {
        if (tableId) {
            registerTable(tableId, tableRef);
            return () => {
                unregisterTable(tableId);
            };
        }
        return undefined;
    }, [tableId, registerTable, unregisterTable]);

    // Sync between multiple tables
    useEffect(() => {
        if (!tableId || !syncWithTableIds?.length || !tableRef.current) {
            return undefined;
        }

        const otherTableRefs = syncWithTableIds
            .map(id => getTableRef(id))
            .filter((ref): ref is React.RefObject<HTMLDivElement> => ref !== undefined);

        if (!otherTableRefs.length) return undefined;

        const syncTables = (from: HTMLDivElement, targetElement: HTMLDivElement): void => {
            const { scrollLeft } = from;
            // Only sync if the scroll position has actually changed
            if (targetElement.scrollLeft !== scrollLeft) {
                targetElement.scrollTo({ left: scrollLeft, behavior: 'auto' });
            }
        };

        const onTableScroll = (): void => {
            const currentTable = tableRef.current;
            if (!currentTable) return;

            const currentScrollLeft = currentTable.scrollLeft;
            // Skip if scroll position hasn't changed
            if (currentScrollLeft === lastScrollLeftRef.current) return;
            lastScrollLeftRef.current = currentScrollLeft;

            // Sync all tables in one go
            otherTableRefs.forEach(ref => {
                const otherTable = ref.current;
                if (otherTable) {
                    syncTables(currentTable, otherTable);
                }
            });
        };

        const onOtherTableScroll = (otherRef: React.RefObject<HTMLDivElement>) => (): void => {
            const currentTable = tableRef.current;
            const otherTable = otherRef.current;
            if (!currentTable || !otherTable) return;

            const currentScrollLeft = otherTable.scrollLeft;
            // Skip if scroll position hasn't changed
            if (currentScrollLeft === lastScrollLeftRef.current) return;
            lastScrollLeftRef.current = currentScrollLeft;

            // Sync all tables in one go
            syncTables(otherTable, currentTable);
            otherTableRefs.forEach(ref => {
                if (ref !== otherRef && ref.current) {
                    syncTables(otherTable, ref.current);
                }
            });
        };

        // Set data attributes for debugging
        if (tableRef.current) {
            tableRef.current.setAttribute('data-table-id', tableId);
            lastScrollLeftRef.current = tableRef.current.scrollLeft;
        }

        // Use passive scroll listeners for better performance
        tableRef.current.addEventListener('scroll', onTableScroll, { passive: true });
        otherTableRefs.forEach(ref => {
            const otherId = syncWithTableIds.find(id => getTableRef(id) === ref);
            if (ref.current && otherId) {
                ref.current.setAttribute('data-table-id', otherId);
                ref.current.addEventListener('scroll', onOtherTableScroll(ref), { passive: true });
            }
        });

        return () => {
            tableRef.current?.removeEventListener('scroll', onTableScroll);
            otherTableRefs.forEach(ref => {
                const otherId = syncWithTableIds.find(id => getTableRef(id) === ref);
                if (ref.current && otherId) {
                    ref.current.removeEventListener('scroll', onOtherTableScroll(ref));
                }
            });
        };
    }, [tableId, syncWithTableIds, getTableRef]);

    const scrollToElement = (element: HTMLElement) => {
        if (!tableRef.current || !element) return;

        isProgrammaticScrollRef.current = true;

        const container = tableRef.current;
        const targetLeft = element.offsetLeft;

        container.scrollTo({
            left: targetLeft,
            behavior: 'smooth',
        });

        setTimeout(() => {
            if (scrollbarRef.current) {
                scrollbarRef.current.scrollLeft = container.scrollLeft;
            }
            isProgrammaticScrollRef.current = false;
        }, 300);
    };

    // Set initial scroll position
    useEffect(() => {
        if (tableRef.current && scrollbarRef.current) {
            scrollbarRef.current.scrollLeft = tableRef.current.scrollLeft;
        }
    }, []);

    // Update scrollbar width based on table width
    useEffect(() => {
        const updateScrollbarWidth = () => {
            if (tableRef.current && scrollbarContentRef.current) {
                const tableWidth = tableRef.current.scrollWidth;
                scrollbarContentRef.current.style.width = `${tableWidth}px`;
            }
        };

        updateScrollbarWidth();

        // Set up a resize observer to update the width when table size changes
        const resizeObserver = new ResizeObserver(updateScrollbarWidth);
        if (tableRef.current) {
            resizeObserver.observe(tableRef.current);
        }

        return () => {
            if (tableRef.current) {
                resizeObserver.unobserve(tableRef.current);
            }
            resizeObserver.disconnect();
        };
    }, [tableRef.current, scrollbarContentRef.current]);

    return {
        tableRef,
        scrollbarRef,
        scrollbarContentRef,
        handleTableScroll,
        scrollToElement,
        createRefHandler,
    };
};
